import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy, where } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { getUserRestaurant } from '../utils/restaurantUtils';
import AdminNavigation from '../components/AdminNavigation';
import { 
  TrendingUp, 
  TrendingDown,
  Euro, 
  ShoppingBag, 
  Clock,
  Award,
  Target,
  BarChart3,
  Loader
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, startOfDay, endOfDay, startOfWeek, startOfMonth, parseISO } from 'date-fns';

function Analytics() {
  const navigate = useNavigate();
  const [restaurantId, setRestaurantId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [dateRange, setDateRange] = useState('week'); // week, month, all
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      const resId = await getUserRestaurant(auth.currentUser.uid);
      setRestaurantId(resId || 'demo-restaurant');
    };
    fetchRestaurant();
  }, [navigate]);

  useEffect(() => {
    if (!restaurantId) return;

    // Get date range
    let startDate = new Date(0); // All time
    if (dateRange === 'week') {
      startDate = startOfWeek(new Date());
    } else if (dateRange === 'month') {
      startDate = startOfMonth(new Date());
    }

    // Subscribe to orders
    const ordersQuery = query(
      collection(db, 'restaurants', restaurantId, 'orders'),
      where('createdAt', '>=', startDate),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
      setLoading(false);
    });

    // Subscribe to menu items
    const menuQuery = query(collection(db, 'restaurants', restaurantId, 'menu'));
    const unsubscribeMenu = onSnapshot(menuQuery, (snapshot) => {
      const menuData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMenuItems(menuData);
    });

    // Subscribe to feedback
    const feedbackQuery = query(
      collection(db, 'restaurants', restaurantId, 'feedback'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeFeedback = onSnapshot(feedbackQuery, (snapshot) => {
      const feedbackData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFeedbacks(feedbackData);
    });

    return () => {
      unsubscribeOrders();
      unsubscribeMenu();
      unsubscribeFeedback();
    };
  }, [restaurantId, dateRange]);

  // Calculate stats
  const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
  const totalOrders = orders.length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const avgRating = feedbacks.length > 0 
    ? feedbacks.reduce((sum, f) => sum + (f.ratings?.overall || 0), 0) / feedbacks.length 
    : 0;

  // Revenue by day - Dynamic based on date range
  const getRevenueChartData = () => {
    let days = 7;
    let formatPattern = 'EEE';
    
    if (dateRange === 'month') {
      days = 30;
      formatPattern = 'MMM dd';
    } else if (dateRange === 'all') {
      // For all time, show last 30 days or all available data
      days = 30;
      formatPattern = 'MMM dd';
    }

    return Array.from({ length: days }, (_, i) => {
      const date = subDays(new Date(), days - 1 - i);
      const dayOrders = orders.filter(order => {
        const orderDate = order.createdAt?.toDate?.();
        return orderDate && format(orderDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
      });
      return {
        date: format(date, formatPattern),
        revenue: dayOrders.reduce((sum, o) => sum + (o.total || 0), 0),
        orders: dayOrders.length
      };
    }).filter(d => dateRange === 'week' || d.orders > 0 || d.revenue > 0); // Show all days for week, only days with data for others
  };

  const revenueChartData = getRevenueChartData();

  // Top selling items
  const itemSales = {};
  orders.forEach(order => {
    order.items?.forEach(item => {
      if (!itemSales[item.id]) {
        itemSales[item.id] = {
          name: item.name,
          quantity: 0,
          revenue: 0
        };
      }
      itemSales[item.id].quantity += item.quantity;
      itemSales[item.id].revenue += item.subtotal || 0;
    });
  });

  const topItems = Object.values(itemSales)
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5);

  // Orders by status
  const statusData = [
    { name: 'Pending', value: orders.filter(o => o.status === 'pending').length, color: '#FCD34D' },
    { name: 'Preparing', value: orders.filter(o => o.status === 'preparing').length, color: '#60A5FA' },
    { name: 'Ready', value: orders.filter(o => o.status === 'ready').length, color: '#34D399' },
    { name: 'Completed', value: orders.filter(o => o.status === 'completed').length, color: '#10B981' }
  ];

  // Peak hours
  const hourlyData = Array.from({ length: 24 }, (_, hour) => {
    const hourOrders = orders.filter(order => {
      const orderDate = order.createdAt?.toDate?.();
      return orderDate && orderDate.getHours() === hour;
    });
    return {
      hour: `${hour}:00`,
      orders: hourOrders.length,
      revenue: hourOrders.reduce((sum, o) => sum + (o.total || 0), 0)
    };
  }).filter(h => h.orders > 0);

  // Category performance
  const categoryData = {};
  orders.forEach(order => {
    order.items?.forEach(item => {
      const menuItem = menuItems.find(m => m.id === item.id);
      const category = menuItem?.category || 'Other';
      if (!categoryData[category]) {
        categoryData[category] = { name: category, revenue: 0, quantity: 0 };
      }
      categoryData[category].revenue += item.subtotal || 0;
      categoryData[category].quantity += item.quantity;
    });
  });

  const categoryChartData = Object.values(categoryData);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {/* Date Range Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-sm text-gray-600">Business insights and performance metrics</p>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {['week', 'month', 'all'].map(range => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  dateRange === range
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range === 'week' && 'Week'}
                {range === 'month' && 'Month'}
                {range === 'all' && 'All'}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs md:text-sm text-gray-600">Total Revenue</p>
              <Euro className="w-4 h-4 md:w-5 md:h-5 text-gray-700" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">€{totalRevenue.toFixed(0)}</p>
            <p className="text-xs md:text-sm text-gray-600 mt-1 flex items-center">
              <TrendingUp className="w-3 h-3 md:w-4 md:h-4 mr-1" />
              {totalOrders} orders
            </p>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs md:text-sm text-gray-600">Avg Order Value</p>
              <Target className="w-4 h-4 md:w-5 md:h-5 text-gray-700" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">€{avgOrderValue.toFixed(2)}</p>
            <p className="text-xs md:text-sm text-gray-600 mt-1">Per transaction</p>
          </div>
        </div>

        {/* Revenue Trend */}
        <div className="card mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-primary-600" />
            Revenue Trend ({dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 30 Days' : 'Last 30 Days'})
          </h2>
          <div className="w-full overflow-hidden">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }} 
                  stroke="#6b7280"
                />
                <YAxis 
                  tick={{ fontSize: 11 }} 
                  stroke="#6b7280"
                />
                <Tooltip 
                  formatter={(value) => `€${value.toFixed(2)}`}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#7c3aed" 
                  strokeWidth={3} 
                  name="Revenue (€)"
                  dot={{ fill: '#7c3aed', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="orders" 
                  stroke="#10b981" 
                  strokeWidth={3} 
                  name="Orders"
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Top Selling Items */}
          <div className="card">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
              <Award className="w-5 h-5 mr-2 text-amber-500" />
              Top Selling Items
            </h2>
            <div className="space-y-3">
              {topItems.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      index === 0 ? 'bg-amber-500 text-white' :
                      index === 1 ? 'bg-gray-400 text-white' :
                      index === 2 ? 'bg-amber-700 text-white' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.quantity} sold</p>
                    </div>
                  </div>
                  <p className="font-bold text-primary-600 text-sm ml-2 flex-shrink-0">€{item.revenue.toFixed(0)}</p>
                </div>
              ))}
              {topItems.length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No sales data yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Category Revenue Distribution */}
          <div className="card">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-primary-600" />
              Category Revenue
            </h2>
            {categoryChartData.length > 0 ? (
              <>
                <div className="w-full overflow-hidden mb-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={categoryChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 11 }} 
                        stroke="#6b7280"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        tick={{ fontSize: 11 }} 
                        stroke="#6b7280"
                        label={{ value: 'Revenue (€)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                      />
                      <Tooltip 
                        formatter={(value) => `€${value.toFixed(2)}`}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                      <Bar 
                        dataKey="revenue" 
                        fill="#7c3aed" 
                        name="Revenue (€)" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {categoryChartData.map((cat, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                      <span className="text-gray-700 truncate">{cat.name}</span>
                      <span className="font-semibold text-gray-900 ml-2">{cat.quantity} sold</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center text-gray-400 py-12">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No category data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Peak Hours - Only show if data exists */}
        {hourlyData.length > 0 && (
          <div className="card mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-500" />
              Busiest Hours
            </h2>
            <div className="w-full overflow-hidden">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 11 }} 
                    stroke="#6b7280"
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }} 
                    stroke="#6b7280"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar 
                    dataKey="orders" 
                    fill="#f59e0b" 
                    name="Orders" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default Analytics;
